/**
 * Supabase Client — centrale client + auth/db/storage helpers
 *
 * Gebruik:
 *   import { supabase, auth, db, storage } from './supabase';
 *
 * Environment variables (in .env):
 *   REACT_APP_SUPABASE_URL=https://api.testwisely.nl
 *   REACT_APP_SUPABASE_ANON_KEY=eyJ...
 */

import { createClient } from '@supabase/supabase-js';
import { maakFetchMetToegangscontrole, naarLoginGedeactiveerd } from '../utils/accountGedeactiveerd';
import { metHerkansing } from '../utils/inlogHerkansing';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('⚠️ Supabase configuratie ontbreekt. Controleer .env bestand.');
}

// Gedeactiveerde docent (migratie 104): de database weigert elk verzoek met 403
// ACCOUNT_GEDEACTIVEERD. Dan één keer lokaal uitloggen en naar /login met melding.
let uitloggenBezig = false;
function bijGedeactiveerd() {
  if (uitloggenBezig) return;
  uitloggenBezig = true;
  supabase.auth.signOut({ scope: 'local' })
    .catch(() => {})
    .finally(() => { naarLoginGedeactiveerd(); uitloggenBezig = false; });
}

export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  global: {
    fetch: maakFetchMetToegangscontrole((...args) => fetch(...args), bijGedeactiveerd),
  },
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Bypass navigator.locks om deadlock te voorkomen.
    // Supabase JS v2 gebruikt navigator.locks in _initialize(), maar als
    // onAuthStateChange callbacks Supabase queries maken (die getSession() nodig
    // hebben), ontstaat een deadlock. Deze simpele lock-functie voorkomt dat.
    lock: async (name, acquireTimeout, fn) => fn(),
  },
  db: {
    schema: 'public',
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// ============================================================
// Auth helpers
// ============================================================

export const auth = {
  /**
   * Inloggen met Entree (OIDC via Supabase GoTrue)
   * Vervangt: signInWithCustomToken / signInWithEmailAndPassword
   */
  signInWithEntree: async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'keycloak', // Entree OIDC via Keycloak provider
      options: {
        redirectTo: `${window.location.origin}/auth/entree-callback`,
        scopes: 'openid profile email',
      },
    });
    if (error) throw error;
    return data;
  },

  /**
   * Inloggen met email/wachtwoord (docenten)
   * Vervangt: signInWithEmailAndPassword
   */
  signInWithEmail: async (email, password) => {
    // Bij drukte (504/503/429) één keer automatisch opnieuw (belastingstest 24-10).
    const { data, error } = await metHerkansing(() => supabase.auth.signInWithPassword({
      email,
      password,
    }));
    if (error) throw error;
    return data;
  },

  /**
   * Registreren met email/wachtwoord (docenten)
   * Vervangt: createUserWithEmailAndPassword
   */
  signUp: async (email, password, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata, // { displayName, schoolBrin, role }
      },
    });
    if (error) throw error;
    return data;
  },

  /**
   * Uitloggen
   * Vervangt: signOut
   */
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  /**
   * Huidige gebruiker ophalen
   * Vervangt: auth.currentUser
   */
  getUser: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  },

  /**
   * Luisteren naar auth state changes
   * Vervangt: onAuthStateChanged
   */
  onAuthStateChange: (callback) => {
    return supabase.auth.onAuthStateChange((event, session) => {
      callback(session?.user || null, event);
    });
  },
};

// ============================================================
// Database helpers — vervangt Firestore
// ============================================================

export const db = {
  /**
   * Alle toetsen van een docent ophalen
   * Vervangt: collection(db, 'tests').where('teacherId', '==', uid)
   */
  getTests: async (teacherId) => {
    const { data, error } = await supabase
      .from('toetsen')
      .select('*')
      .eq('docent_id', teacherId)
      .is('verwijderd_op', null)
      .order('aangemaakt_op', { ascending: false });
    if (error) throw error;
    return data;
  },

  /**
   * Toets ophalen op ID
   * Vervangt: doc(db, 'tests', testId)
   */
  getTest: async (testId) => {
    const { data, error } = await supabase
      .from('toetsen')
      .select('*, vragen(*)')
      .eq('id', testId)
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Toets ophalen op 6-cijfer code
   * Vervangt: collection(db, 'tests').where('code', '==', code)
   */
  getTestByCode: async (code) => {
    const { data, error } = await supabase
      .from('toetsen')
      .select('*, vragen(*)')
      .eq('toets_code', code)
      .eq('is_actief', true)
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Toets aanmaken
   * Vervangt: addDoc(collection(db, 'tests'), data)
   */
  createTest: async (testData) => {
    const { data, error } = await supabase
      .from('toetsen')
      .insert(testData)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Toets bijwerken
   * Vervangt: updateDoc(doc(db, 'tests', testId), data)
   */
  updateTest: async (testId, updates) => {
    const { data, error } = await supabase
      .from('toetsen')
      .update(updates)
      .eq('id', testId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Sessies ophalen voor een toets
   * Vervangt: collection(db, 'sessions').where('testId', '==', testId)
   */
  getSessions: async (testId) => {
    const { data, error } = await supabase
      .from('sessies')
      .select('*')
      .eq('toets_id', testId)
      .order('gestart_op', { ascending: false });
    if (error) throw error;
    return data;
  },

  /**
   * Sessie aanmaken (leerling start toets)
   * Vervangt: addDoc(collection(db, 'sessions'), data)
   */
  createSession: async (sessionData) => {
    const { data, error } = await supabase
      .from('sessies')
      .insert(sessionData)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Sessie bijwerken (antwoorden opslaan)
   * Vervangt: updateDoc(doc(db, 'sessions', sessionId), data)
   */
  updateSession: async (sessionId, updates) => {
    const { data, error } = await supabase
      .from('sessies')
      .update(updates)
      .eq('id', sessionId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Realtime luisteren naar sessie-updates
   * Vervangt: onSnapshot(doc(db, 'sessions', sessionId), callback)
   */
  subscribeToSessions: (testId, callback) => {
    return supabase
      .channel(`sessions-${testId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'sessies',
        filter: `toets_id=eq.${testId}`,
      }, (payload) => {
        callback(payload);
      })
      .subscribe();
  },

  /**
   * Resultaten ophalen
   * Vervangt: collection(db, 'results').where('teacherId', '==', uid)
   */
  getResults: async (teacherId) => {
    const { data, error } = await supabase
      .from('resultaten')
      .select('*')
      .eq('docent_id', teacherId)
      .order('beoordeeld_op', { ascending: false });
    if (error) throw error;
    return data;
  },

  /**
   * Vragenbank ophalen
   * Vervangt: collection(db, 'questionBank').where('teacherId', '==', uid)
   */
  getQuestionBank: async (teacherId) => {
    const { data, error } = await supabase
      .from('vragenbank')
      .select('*')
      .eq('docent_id', teacherId)
      .is('verwijderd_op', null)
      .order('aangemaakt_op', { ascending: false });
    if (error) throw error;
    return data;
  },

  /**
   * Klassen ophalen voor een school
   * Vervangt: collection(db, 'schools', brin, 'classes')
   */
  getClasses: async (schoolId) => {
    const { data, error } = await supabase
      .from('klassen')
      .select('*, klas_leerlingen(leerling_id)')
      .eq('school_id', schoolId)
      .is('verwijderd_op', null);
    if (error) throw error;
    return data;
  },

  /**
   * Studenten ophalen
   * Vervangt: collection(db, 'students')
   */
  getStudents: async (schoolId) => {
    const { data, error } = await supabase
      .from('leerlingen')
      .select('*')
      .eq('school_id', schoolId)
      .is('verwijderd_op', null);
    if (error) throw error;
    return data;
  },
};

// ============================================================
// Storage helpers
// ============================================================

export const storage = {
  /**
   * Afbeelding uploaden
   */
  uploadImage: async (file, path) => {
    const { data, error } = await supabase.storage
      .from('vraag-afbeeldingen')
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false,
      });
    if (error) throw error;

    const { data: urlData } = supabase.storage
      .from('vraag-afbeeldingen')
      .getPublicUrl(data.path);

    return urlData.publicUrl;
  },

  /**
   * Afbeelding verwijderen
   */
  deleteImage: async (path) => {
    const { error } = await supabase.storage
      .from('vraag-afbeeldingen')
      .remove([path]);
    if (error) throw error;
  },
};

export default supabase;
